/** Mini App API client: initData sign-in, 15-minute bearer sessions with silent refresh. */

import { claimForCurrentClick } from '@exam-prep/ui';
import { env } from './env';

const BASE = env.VITE_API_URL;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

let token = '';
let onUnauthorized: (() => void) | null = null;

/** Called when a signed-in request gets a 401 (the session expired or was revoked). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

let expiresAt = 0;
let refreshing: Promise<void> | null = null;

async function raw<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth && token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? null : JSON.stringify(body),
  });
  const text = await res.text();
  const json = (text ? JSON.parse(text) : null) as
    (T & { error?: { code?: string; message?: string } }) | null;
  if (res.status === 401 && auth) onUnauthorized?.();
  if (!res.ok)
    throw new ApiError(
      res.status,
      json?.error?.code ?? `http_${String(res.status)}`,
      json?.error?.message ?? res.statusText,
    );
  return json as T;
}

/** A request started from a Button tap keeps that button disabled with a spinner until it settles. */
function claimed<T>(request: Promise<T>): Promise<T> {
  claimForCurrentClick(request);
  return request;
}

export function signIn(productKey: string, initData: string) {
  return claimed(startSession(productKey, initData));
}

async function startSession(productKey: string, initData: string) {
  const r = await raw<{
    token: string;
    expiresAt: string;
    firstName: string | null;
    examKey: string | null;
  }>('POST', '/app/session', { productKey, initData }, false);
  token = r.token;
  expiresAt = Date.parse(r.expiresAt);
  return r;
}

async function ensureFresh(): Promise<void> {
  if (!token || Date.now() < expiresAt - 120_000) return;
  refreshing ??= raw<{ token: string; expiresAt: string }>('POST', '/app/session/refresh')
    .then((r) => {
      token = r.token;
      expiresAt = Date.parse(r.expiresAt);
    })
    .finally(() => {
      refreshing = null;
    });
  await refreshing;
}

export function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  return claimed(ensureFresh().then(() => raw<T>(method, path, body)));
}
